import * as React from 'react';
import { formatStoryLabel } from './labels';
import type { StoryLabels } from './labels';
import type { StoryPosterStyle } from './stories-theme';
import { StoryPortrait, StoryTape } from './story-bits';
import type { StoryLang, StoryUnit, StoryWoman } from './story-types';
import { storyFirstName, storyLangAttr, storyWomanQuote } from './story-utils';

export interface StoryClosingPageProps {
  woman: StoryWoman;
  unit?: StoryUnit | null;
  lang: StoryLang;
  labels: StoryLabels;
  posterStyle: StoryPosterStyle;
  /** preview = the buttons are shown but do nothing */
  inert?: boolean;
  /** false = still picture (thumbnail): buttons cannot be focused */
  interactive?: boolean;
  onStartClass?: () => void;
  onViewMural?: () => void;
}

/** Fixed last page of every story: her poster on the wall and what to do next. */
export function StoryClosingPage({
  woman,
  unit,
  lang,
  labels,
  posterStyle: us,
  inert = false,
  interactive = true,
  onStartClass,
  onViewMural,
}: StoryClosingPageProps) {
  const quote = storyWomanQuote(woman, lang);
  const firstClassName = unit?.firstClassName?.trim() || '';
  const showClass = Boolean(unit && firstClassName && (onStartClass || inert));
  const showMural = Boolean(onViewMural || inert);
  const dead = inert || !interactive;
  const btn = { type: 'button' as const, tabIndex: dead ? -1 : undefined, 'aria-disabled': dead || undefined };

  return (
    <div className="zs-pg-in" data-testid="story-closing">
      <h2 className="zs-kick">{labels.closingKicker}</h2>
      <p className="zs-why" style={{ fontSize: 24, marginTop: 10 }}>
        {formatStoryLabel(labels.onWall, { name: storyFirstName(woman.name) })}
      </p>
      <div className="zs-mini" style={{ background: us.bg, color: us.text }}>
        <StoryTape left={14} top={-6} rotate={-10} width={38} color={us.tape} />
        <StoryTape right={18} top={-4} rotate={6} width={28} color={us.tape} />
        {unit ? <div className="zs-u">{formatStoryLabel(labels.unitN, { n: unit.order })}</div> : null}
        {quote.text ? (
          <div className="zs-mq" lang={storyLangAttr(quote.lang)}>
            {quote.text}
          </div>
        ) : null}
        <div className="zs-who">
          <span className="zs-ring" style={{ '--zs-ringc': `${us.text}88` } as React.CSSProperties}>
            <span className="zs-gap" style={{ background: us.bg }}>
              <StoryPortrait url={woman.portraitUrl} fallback={woman.name.trim().charAt(0).toUpperCase()} color={us.text} size={34} />
            </span>
          </span>
          <div>
            <div className="zs-nm">— {woman.name}</div>
            {woman.years ? <div className="zs-yr">{woman.years}</div> : null}
          </div>
        </div>
      </div>
      {showClass && unit ? (
        <>
          <div className="zs-cls zs-int">
            <div className="zs-k2">{formatStoryLabel(labels.firstClass, { n: unit.classTotal })}</div>
            <div className="zs-n2">{firstClassName}</div>
          </div>
          <button
            {...btn}
            className={`zs-tactile zs-bigcta zs-int${dead ? ' zs-inert' : ''}`}
            data-testid="story-start-class"
            onClick={dead ? undefined : onStartClass}
          >
            <span>{labels.startClass}</span>
            <small>{firstClassName}</small>
          </button>
        </>
      ) : null}
      {showMural ? (
        <button
          {...btn}
          className={`zs-quiet zs-int${dead ? ' zs-inert' : ''}`}
          data-testid="story-see-wall"
          onClick={dead ? undefined : onViewMural}
        >
          {labels.seeWall}
        </button>
      ) : null}
      <p className="zs-rehint">{labels.rereadHint}</p>
    </div>
  );
}
